import { useEffect, useState, Fragment } from 'react';
import {
	Alert,
	FormControl,
	FormControlLabel,
	FormLabel,
	Checkbox,
	FormHelperText,
	FormGroup,
	RadioGroup,
	Typography,
	TextField,
	Skeleton,
	Radio,
	Stack,
	Box,
} from '@mui/material';
import type { FormikProps } from 'formik';

import { useGetTestsQuery } from '../../../slices/apis/testApi';

import { type TestProps, type Status, Test, Request } from '../../../models';
import { fCurrency } from '../../../utils/formatNumber';

interface TestsComponentProps {
	formik: FormikProps<{
		supplierId: string;
		tests: TestProps[];
		status: Status;
		extraInfo?: string | null;
	}>;
}

const followUpQuestionAnswers = [
	{
		title: 'Sedentary Work',
		subtitle: 'Mainly sitting. Lifting up to 4.5 kg occasionally, minimal force otherwise.',
	},
	{
		title: 'Light Work',
		subtitle:
			'Lifting up to 9 kg occasionally or 4.5 kg frequently. May involve standing, walking, or constant arm/leg use even with minimal weight.',
	},
	{
		title: 'Medium Work',
		subtitle: 'Lifting 9–22.75 kg occasionally, 4.5–11.34 kg frequently, or up to 4.5 kg constantly.',
	},
	{
		title: 'Heavy Work',
		subtitle: 'Lifting 22.7–45.5 kg occasionally, 11.34–22.75 kg frequently, or up to 4.5-9 kg constantly.',
	},
	{
		title: 'Very Heavy Work',
		subtitle: 'Lifting over 45.5 kg occasionally, over 22.7 kg frequently, or over 9 kg constantly.',
	},
];

export const Tests: React.FC<TestsComponentProps> = ({ formik }) => {
	const { data: tests } = useGetTestsQuery();
	const [, setFollowUpQuestion] = useState(false);

	const [followUpQuestionAnswer, setFollowUpQuestionAnswer] = useState(formik.values.extraInfo);
	const [followUpQuestionCustomAnswer, setFollowUpQuestionCustomAnswer] = useState(() => {
		const val = formik.values.extraInfo ?? '';

		if (followUpQuestionAnswers.map((answer) => answer.subtitle).includes(val)) {
			return '';
		}

		return val;
	});

	const ifTestChecked = Request.ifMarkTestChecked(formik.values.tests);

	useEffect(() => {
		const iffollowUpQuestionTest = formik.values.tests.some((test) => Test.isFollowUpQuestionTest(test.id));
		setFollowUpQuestion(iffollowUpQuestionTest);

		if (!iffollowUpQuestionTest) {
			setFollowUpQuestionAnswer('');
			setFollowUpQuestionCustomAnswer('');
		}
	}, [formik.values.tests]);

	const onTestChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.checked
			? [
					...formik.values.tests,
					{
						id: e.target.name,
					},
				]
			: formik.values.tests.filter((test) => Number(test.id) !== Number(e.target.name));

		formik.setValues({
			...formik.values,
			tests: newValue.map(Test.normalise),
			// reset test centre when we change tests
			supplierId: '',
		});
	};

	const onFollowUpQuestionAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFollowUpQuestionAnswer(e.target.value);
		formik.setFieldValue('extraInfo', e.target.value);
		setFollowUpQuestionCustomAnswer('');
	};

	const onFollowUpQuestionCustomAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setFollowUpQuestionCustomAnswer(e.target.value);
		formik.setFieldValue('extraInfo', e.target.value);
		setFollowUpQuestionAnswer('');
	};

	const ifError = Boolean(formik.touched.tests) && Boolean(formik.errors.tests);

	return (
		<Box sx={{ overflowX: 'auto', maxWidth: '100%' }}>
			{!tests ? (
				<Stack width={240} spacing={3}>
					<Skeleton variant="rectangular" animation="wave" height={18} />
					<Skeleton variant="rectangular" animation="wave" height={18} />
					<Skeleton variant="rectangular" animation="wave" height={18} />
					<Skeleton variant="rectangular" animation="wave" height={18} />
				</Stack>
			) : (
				<FormControl
					margin="normal"
					component="fieldset"
					variant="standard"
					error={ifError}
					sx={{ mt: 0, width: '100%' }}
				>
					<Alert severity="info" sx={{ mb: 2 }}>
						If you need help with test selection, please contact us at <a href="tel:1300391947">1300 391 947</a> or{' '}
						<a href="mailto:pes@ausrehab.com">pes@ausrehab.com</a>.
					</Alert>
					{ifError && <FormHelperText>{formik.errors.tests as string}</FormHelperText>}
					<FormGroup>
						{tests.map((test) => {
							const isFollowUp = Test.isFollowUpQuestionTest(test.id);
							const isChecked = ifTestChecked(test);

							return (
								<Box key={test.id}>
									<FormControlLabel
										control={
											<Checkbox
												name={test.id.toString()}
												checked={isChecked}
												onChange={onTestChange}
												disabled={Request.disableTestsEditing(formik.values.status)}
											/>
										}
										label={
											<Box
												sx={{
													display: 'flex',
													width: '100%',
													justifyContent: 'space-between',
													minWidth: {
														xs: 320,
														sm: 480,
													},
												}}
											>
												<Box component="span">{test.name}</Box>
												<Box component="span">{fCurrency(test.price)}</Box>
											</Box>
										}
									/>

									{isFollowUp && isChecked && (
										<Box sx={{ pl: 4, mt: 1 }}>
											<FormLabel>What are the physical demand of the role you want to test?</FormLabel>
											<RadioGroup value={followUpQuestionAnswer} onChange={onFollowUpQuestionAnswerChange}>
												{followUpQuestionAnswers.map((answer) => (
													<Fragment key={answer.title}>
														<FormControlLabel
															value={answer.subtitle}
															label={<Typography variant="subtitle1">{answer.title}</Typography>}
															control={<Radio />}
														/>
														<Typography sx={{ pl: 4 }}>{answer.subtitle}</Typography>
													</Fragment>
												))}
											</RadioGroup>
											<TextField
												multiline
												rows={3}
												value={followUpQuestionCustomAnswer}
												onChange={onFollowUpQuestionCustomAnswerChange}
												placeholder="Custom - tell us what you want to assess"
												inputProps={{ maxLength: 100 }}
												fullWidth
												margin="dense"
											/>
										</Box>
									)}
								</Box>
							);
						})}
					</FormGroup>
				</FormControl>
			)}
		</Box>
	);
};
